import React, { useState } from "react";
import {
    Search,
    Home,
    Clock,
    Users,
    Star,
    ChevronDown,
    Plus,
    MessageSquare,
    FileText,
    CheckSquare,
    Layout,
    Settings,
    MoreHorizontal,
    Bell,
    Sidebar as SidebarIcon
} from "lucide-react";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

interface WorkspaceLayoutProps {
    children: React.ReactNode;
}

const WorkspaceLayout: React.FC<WorkspaceLayoutProps> = ({ children }) => {
    const [location] = useLocation();
    // Default sidebar open
    const [sidebarOpen, setSidebarOpen] = useState(true);

    const sidebarItems = [
        { icon: Home, label: "Home", href: "/workspace" },
        { icon: Search, label: "Search", href: "#" },
        { icon: Bell, label: "Updates", href: "#", badge: 3 },
        { icon: Settings, label: "Settings", href: "/settings" },
    ];

    const favorites = [
        { icon: CheckSquare, label: "My tasks", href: "/tasks" },
        { icon: Users, label: "Rose Compás", href: "#" },
        { icon: FileText, label: "Pitch Deck (Series A)", href: "#" },
    ];

    const suggested = [
        { label: "Interview Cara Bina", emoji: "📋" },
        { label: "Cold email campaign", emoji: "📧" },
        { label: "Fundraising", emoji: "💰" },
        { label: "Engineering", emoji: "🛠️" },
        { label: "Sprint (2026 Q2)", emoji: "📅" },
    ];

    return (
        <div className="flex h-screen bg-gray-100 overflow-hidden text-[#1a1a1a] font-sans">
            {/* Sidebar - hidden on mobile by default */}
            <aside
                className={`${sidebarOpen ? "w-64" : "w-0"
                    } fixed md:relative z-40 h-full transition-all duration-300 ease-in-out bg-[#f7f7f5] border-r border-[#ececeb] flex flex-col`}
            >
                <div className="p-4 flex flex-col h-full overflow-y-auto custom-scrollbar">
                    {/* Workspace Header */}
                    <div className="flex items-center gap-3 mb-6 px-2">
                        <div className="w-8 h-8 bg-shiftleft-600 rounded flex items-center justify-center text-white font-bold text-lg">
                            A
                        </div>
                        <span className="font-semibold text-[15px]">Alpine</span>
                        <ChevronDown className="w-4 h-4 text-gray-400 ml-auto" />
                    </div>

                    {/* Main Nav */}
                    <div className="space-y-0.5 mb-6 text-[14px]">
                        {sidebarItems.map((item) => (
                            <Link key={item.label} href={item.href}>
                                <div
                                    className={`flex items-center gap-3 px-2 py-1.5 rounded-md cursor-pointer transition-colors group ${location === item.href ? "bg-[#ebebeb]" : "hover:bg-[#ebebeb]"
                                        }`}
                                >
                                    <item.icon className="w-[18px] h-[18px] text-gray-500 group-hover:text-gray-900" />
                                    <span className={`${location === item.href ? "font-medium" : "text-gray-700"} group-hover:text-gray-900`}>
                                        {item.label}
                                    </span>
                                    {item.badge && (
                                        <span className="ml-auto bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                                            {item.badge}
                                        </span>
                                    )}
                                </div>
                            </Link>
                        ))}
                    </div>

                    {/* Favorites */}
                    <div className="mb-6">
                        <div className="flex items-center justify-between px-2 mb-1 group">
                            <span className="text-[12px] font-semibold text-gray-500 uppercase tracking-wider">Favorites</span>
                            <Plus className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 cursor-pointer" />
                        </div>
                        <div className="space-y-0.5 text-[14px]">
                            {favorites.map((item) => (
                                <div key={item.label} className="flex items-center gap-3 px-2 py-1.5 rounded-md cursor-pointer hover:bg-[#ebebeb] text-gray-700 group transition-colors">
                                    <item.icon className="w-[18px] h-[18px] text-gray-500 group-hover:text-gray-900" />
                                    <span className="truncate group-hover:text-gray-900">{item.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* People */}
                    <div className="mb-6">
                        <div className="flex items-center justify-between px-2 mb-1 group">
                            <span className="text-[12px] font-semibold text-gray-500 uppercase tracking-wider">People</span>
                            <Plus className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 cursor-pointer" />
                        </div>
                        <div className="space-y-0.5 text-[14px]">
                            {["Matt R. Horn", "ChatGPT", "Mason and Elle"].map((person) => (
                                <div key={person} className="flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer hover:bg-[#ebebeb] text-gray-700 group transition-colors">
                                    <Avatar className="w-5 h-5">
                                        <AvatarFallback className="text-[10px] bg-shiftleft-100 text-shiftleft-600">{person.charAt(0)}</AvatarFallback>
                                    </Avatar>
                                    <span className="truncate group-hover:text-gray-900">{person}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Suggested */}
                    <div className="mb-6">
                        <div className="flex items-center justify-between px-2 mb-1 group">
                            <span className="text-[12px] font-semibold text-gray-500 uppercase tracking-wider">Suggested</span>
                        </div>
                        <div className="space-y-0.5 text-[14px]">
                            {suggested.map((item) => (
                                <div key={item.label} className="flex items-center gap-3 px-2 py-1.5 rounded-md cursor-pointer hover:bg-[#ebebeb] text-gray-700 group transition-colors">
                                    <span className="w-[18px] h-[18px] flex items-center justify-center text-[14px]">{item.emoji}</span>
                                    <span className="truncate group-hover:text-gray-900">{item.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="mt-auto pt-4 border-t border-[#ececeb]">
                        <div className="flex items-center gap-3 px-2 py-2 rounded-md cursor-pointer hover:bg-[#ebebeb] transition-colors">
                            <Avatar className="w-8 h-8">
                                <AvatarImage src="/avatar.png" />
                                <AvatarFallback>JD</AvatarFallback>
                            </Avatar>
                            <div className="flex flex-col overflow-hidden">
                                <span className="text-[14px] font-medium truncate">John Doe</span>
                                <span className="text-[12px] text-gray-500 truncate">john@shiftleft.digital</span>
                            </div>
                            <MoreHorizontal className="w-4 h-4 text-gray-400 ml-auto" />
                        </div>
                    </div>
                </div>
            </aside>

            {/* Main Content Area */}
            <main className="flex-1 overflow-y-auto relative bg-gray-100">
                {/* Top Header */}
                <header className="sticky top-0 z-10 h-12 flex items-center px-4 bg-white/80 backdrop-blur-sm border-b border-[#f1f1ef]">
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-gray-500 mr-2"
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                    >
                        <SidebarIcon className="w-4 h-4" />
                    </Button>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                        <span className="hover:text-gray-900 cursor-pointer">Workspace</span>
                        <span className="text-gray-300">/</span>
                        <span className="font-medium text-gray-900">Home</span>
                    </div>
                    <div className="ml-auto flex items-center gap-4">
                        <Button variant="ghost" size="sm" className="h-8 text-gray-500 hover:text-gray-900">Share</Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-500">
                            <MessageSquare className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-500">
                            <Clock className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-500">
                            <Star className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-500">
                            <MoreHorizontal className="w-4 h-4" />
                        </Button>
                    </div>
                </header>

                {/* Board-Style Content */}
                <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 md:p-8 min-h-[calc(100vh-160px)]">
                        {children}
                    </div>
                </div>
            </main>
        </div>
    );
};

export default WorkspaceLayout;
